import React from 'react';
import * as Sentry from '@sentry/react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';

import { ENVIRONMENT } from '@/constants';
import { scrubConsoleBreadcrumb, scrubEvent, shouldDropEvent, type ConsoleBreadcrumbLike } from '@/utils/sentry-scrub';

Sentry.init({
  dsn: ENVIRONMENT.VITE_FE_SENTRY_DSN,
  environment: ENVIRONMENT.VITE_ENVIRONMENT,

  // PHI safety: RxErrors nested inside console args are only safe because
  // normalization collapses them at depth 3 before beforeSend — scrubArgument does
  // not recurse. Raising this re-opens the note-content leak.
  normalizeDepth: 3,

  integrations: [
    // Report console.error calls as Sentry events; scrubbed in beforeSend.
    Sentry.captureConsoleIntegration({ levels: ['error'] }),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      // Replay records console output through its own pipeline, bypassing beforeSend —
      // apply the same scrub policy so raw RxErrors logged anywhere can't put note
      // content into recordings (HIPAA).
      beforeAddRecordingEvent: (event) => {
        // The SDK's console-frame data type has no index signature, so cast to the
        // helper's structural shape (the payload matches at runtime).
        if (event.data?.tag === 'breadcrumb') {
          scrubConsoleBreadcrumb(event.data.payload as ConsoleBreadcrumbLike);
        }
        return event;
      },
    }),
  ],

  // Sends logs to Sentry
  enableLogs: true,

  // HIPAA minimum necessary: don't attach client IP, headers, or cookies.
  // User attribution comes from Sentry.setUser in ProtectedRoutes. Browser/OS context
  // is computed client-side and unaffected by this flag.
  sendDefaultPii: false,

  // Capture 100% of transactions for tracing
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  beforeSend(event) {
    if (shouldDropEvent(event)) return null;

    return scrubEvent(event);
  },
});
